import countries from "@/assets/countries.json";
import languages from "@/assets/languages.json";

type Entry = { code: string; name: string };

const cache = new Map<string, Intl.DisplayNames | null>();

/** One `Intl.DisplayNames` per kind and language — they are built on every render otherwise. */
const displayNames = (type: "language" | "region", uiLanguage: string) => {
  const key = `${type}:${uiLanguage}`;
  if (!cache.has(key)) {
    let names: Intl.DisplayNames | null = null;
    try {
      names = new Intl.DisplayNames([uiLanguage], { type, fallback: "none" });
    } catch {
      // An engine without DisplayNames, or a language it rejects.
    }
    cache.set(key, names);
  }
  return cache.get(key) ?? null;
};

const nameOf = (
  type: "language" | "region",
  code: string,
  uiLanguage: string,
) => {
  try {
    return displayNames(type, uiLanguage)?.of(code);
  } catch {
    // A code DisplayNames rejects outright.
    return undefined;
  }
};

const capitalize = (name: string, uiLanguage: string) =>
  name.charAt(0).toLocaleUpperCase(uiLanguage) + name.slice(1);

/** "fr-FR" and "fr" name the same language. */
const baseLanguage = (code: string) => code.toLowerCase().split(/[-_]/)[0];

/** "FR" → 🇫🇷, from the two regional-indicator letters; `""` for anything else. */
export const regionFlag = (code?: string | null) =>
  code && /^[A-Za-z]{2}$/.test(code)
    ? String.fromCodePoint(
        ...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
      )
    : "";

/**
 * The flag a language is usually shown with — "fr" → 🇫🇷, "en" → 🇺🇸 — from the
 * locale's likely region. Nothing when there is none (a script, a continent).
 */
export const languageFlag = (code?: string | null) => {
  try {
    return regionFlag(new Intl.Locale(code || "").maximize().region);
  } catch {
    return "";
  }
};

/** A country in the interface's language, else its code. */
export const countryName = (code: string, uiLanguage = "fr") =>
  nameOf("region", code.toUpperCase(), uiLanguage) || code;

/**
 * A language in the interface's language, else the English name of `languages.json`,
 * else its code.
 */
export const languageName = (code: string, uiLanguage = "fr") => {
  const name = nameOf("language", code, uiLanguage);
  if (name) return capitalize(name, uiLanguage);
  return (
    languages.find((lang) => baseLanguage(lang.code) === baseLanguage(code))
      ?.name || code.toUpperCase()
  );
};

/**
 * `languages.json` and `countries.json` carry English names, and the selects
 * printed them as they were: "French" in a French interface. Intl knows every
 * one of these codes in the interface's language; the file's own name is only
 * the fallback for a code it does not.
 */
export const localizedNames = (
  entries: Entry[],
  type: "language" | "region",
  uiLanguage: string,
): Entry[] => {
  const collator = new Intl.Collator(uiLanguage);
  return entries
    .map((entry) => {
      const name = nameOf(type, entry.code, uiLanguage);
      return {
        code: entry.code,
        name: name ? capitalize(name, uiLanguage) : entry.name,
      };
    })
    .sort((a, b) => collator.compare(a.name, b.name));
};

export const localizedLanguages = (uiLanguage: string) =>
  localizedNames(languages, "language", uiLanguage);

export const localizedCountries = (uiLanguage: string) =>
  localizedNames(countries, "region", uiLanguage);
